// ANSWERS TYPED INTO A WORKED EXAMPLE, KEPT ON THIS DEVICE.
//
// The sample worksheets were read-only, for a reason that was true: there is no review
// behind them to write to, and no facility to write it against. But "cannot be saved to a
// facility" is not the same as "cannot be typed into", and the second is what a person
// evaluating the product wants to do — answer a row, see the tally move, see what the
// coverage split does when a reviewer disagrees with the data.
//
// So typing works and persists HERE, per browser, and nowhere else. Three things follow
// from that and all three are stated on screen rather than left to be discovered:
//
//   * it is not saved to any facility, and no API call is made;
//   * it does not follow the person to another browser or another machine;
//   * it is not PHI, because the residents are invented — which is the only reason
//     keeping it in browser storage is acceptable at all. Nothing in here may ever be
//     pointed at live data.
//
// Every access is wrapped. localStorage throws outright in a private window with site data
// blocked, and returns null when storage was cleared, so a reader that trusts it takes the
// whole worksheet down with it — in the one mode that exists so somebody can try the
// product without a facility.

const PREFIX = "survai.pia.sample.v1";

const key = (aspect, residentId) => `${PREFIX}.${aspect || "UNKNOWN"}.${residentId || "UNKNOWN"}`;

/**
 * What was typed for this sample resident, or null if nothing was.
 *
 * Returns null on a parse failure as well as on an absence. A half-written or
 * hand-edited value is not partially recovered: the shapes here feed a worksheet's
 * drafts, and a malformed draft would render as an answer nobody gave.
 */
export function loadSampleDraft(aspect, residentId) {
  try {
    const raw = window.localStorage.getItem(key(aspect, residentId));
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== "object") return null;
    return {
      drafts: v.drafts && typeof v.drafts === "object" ? v.drafts : {},
      reviewer: typeof v.reviewer === "string" ? v.reviewer : "",
      reviewedOn: typeof v.reviewedOn === "string" ? v.reviewedOn : "",
      comments: typeof v.comments === "string" ? v.comments : "",
      savedAt: typeof v.savedAt === "string" ? v.savedAt : null,
    };
  } catch {
    return null;
  }
}

/** Keep what was typed. Returns whether it was actually kept, so the caller can say so. */
export function saveSampleDraft(aspect, residentId, { drafts, reviewer, reviewedOn, comments }) {
  try {
    window.localStorage.setItem(
      key(aspect, residentId),
      JSON.stringify({
        drafts: drafts || {},
        reviewer: reviewer || "",
        reviewedOn: reviewedOn || "",
        comments: comments || "",
        savedAt: new Date().toISOString(),
      }),
    );
    return true;
  } catch {
    // Quota, a private window, or blocked site data. The caller reports it rather than
    // showing "Saved." over a write that did not happen — the same distinction between
    // "did it" and "could not" that the rest of this codebase keeps.
    return false;
  }
}

export function clearSampleDraft(aspect, residentId) {
  try {
    window.localStorage.removeItem(key(aspect, residentId));
    return true;
  } catch {
    return false;
  }
}

/** Every sample draft, for the "clear all" action. Best effort by construction. */
export function clearAllSampleDrafts() {
  try {
    const doomed = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX)) doomed.push(k);
    }
    for (const k of doomed) window.localStorage.removeItem(k);
    return doomed.length;
  } catch {
    return 0;
  }
}
